import { useState, useEffect, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player/lazy';

import { useAuth, useCourses } from 'contexts';
import { DetailContentInterface } from 'types';
import { getFolderLog, registerFolderLog } from 'services';

import './style.scss';
import { ReadStream } from 'tty';

type VideoProps = {
  content: DetailContentInterface;
  className?: string;
  [key: string]: any;

  prevLesson?: boolean | null;
  nextLesson?: boolean | null;
  onPrev?: () => void;
  onNext?: () => void;
};

export function Video({ content, className, prevLesson, nextLesson, onNext, onPrev }: VideoProps) {
  const rpRef = useRef<any>();

  const { user } = useAuth();
  const { getFullProgress } = useCourses();

  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState<any>();
  const [ready, setReady] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    setDone(false);
    setReady(false);
    setFailed(false);
    setDuration(0);
    setLog(undefined);
  }, [content]);

  useEffect(() => {
    if (!user) return;

    getFolderLog(user._id, content._id)
      .then(data => {
        if (data.length) {
          setLog(data[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [content, user]);

  // useEffect(() => {
  //   if (log?.status === 'in_progress' && log?.extra?.played) {
  //     rpRef.current?.seekTo(log?.extra?.played, 'fraction');
  //   }
  // }, [log]);

  const handleProgress = useCallback(
    (state: { playedSeconds: number; played: number; loadedSeconds: number; loaded: number }) => {
      if (!user || !ready || failed) return;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (!Number.isFinite(state.played) || state.played <= 0) return;

      if ((!log || log?.status === 'in_progress') && !done) {
        if (state.played > 0.95) {
          registerFolderLog(user._id, content._id, 'done').finally(() => {
            getFullProgress();
          });

          setDone(true);
        } else {
          registerFolderLog(user._id, content._id, 'in_progress', { played: state.played });
        }
      }
    },
    [content, done, duration, failed, getFullProgress, log, ready, user],
  );

  const handleReady = useCallback(() => {
    if (log?.status === 'in_progress' && log?.extra?.played) {
      rpRef.current?.seekTo(log?.extra?.played, 'fraction');
    }

    if (log?.status === 'done') {
      setDone(true);
    }

    setReady(true);
  }, [log]);

  if (!content || !content.data || loading) return null;

  const url = typeof content.data.url === 'string' ? content.data.url : '';
  const unavailable = failed || !url;

  return (
    <>
      {unavailable ? (
        <div className="video-component video-unavailable flex flex-col items-center justify-center text-center bg-gray-100 rounded-xl p-8">
          <p className="text-lg font-medium text-[#181b1a]">Este vídeo não está disponível no momento.</p>
          <p className="mt-2 text-sm text-gray-500">
            O conteúdo pode ter sido removido ou estar temporariamente fora do ar. Avise o administrador do curso e siga para a
            próxima aula.
          </p>
        </div>
      ) : (
        <div className={`video-component ${className || ''}`}>
          <ReactPlayer
            ref={rpRef}
            url={url}
            className="player"
            playing
            controls
            width="100%"
            height="100%"
            onProgress={handleProgress}
            onReady={handleReady}
            onDuration={setDuration}
            onError={() => setFailed(true)}
            config={{
              youtube: {
                playerVars: { showinfo: 0, controls: 1 },
              },
            }}
          />
        </div>
      )}

      {(!!prevLesson || !!nextLesson) && (
        <div className="flex items-center justify-between my-8">
          <div>
            {prevLesson && (
              <button
                onClick={onPrev}
                className="flex items-center gap-2 text-lg font-medium text-gray-500 hover:text-[#181b1a] transition-colors"
              >
                ← Anterior
              </button>
            )}
          </div>

          <div>
            {nextLesson && (
              <button
                onClick={onNext}
                className="flex items-center gap-2 px-5 py-2.5 bg-orange text-white rounded-md text-lg font-medium hover:bg-orange/90 transition-colors"
              >
                Próximo →
              </button>
            )}
          </div>
        </div>
      )}

      {!!content?.data?.description && (
        <p
          className="description mb-3 text-left mt-7 text-[#181b1a]"
          dangerouslySetInnerHTML={{ __html: content.data.description }}
        />
      )}
    </>
  );
}
